import { CircularProgress, Stack } from '@mui/joy';
import { forwardRef, useMemo } from 'react';

import { AlternateVersionHelper } from 'core/helpers';
import { IPartFinderReel } from 'partFinder/types';
import { LineDetails } from 'quotes/components/QuotePartUpsertLine/UpsertLine';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { useGetQuote } from 'quotes/hooks';
import { SelectedQuotePartProvider } from 'quotes/states';
import { ICustomerQuote, IQuotePartLineForm } from 'quotes/types';

interface IProps {
  reel: IPartFinderReel;
  quoteId: number;
  entityType: QuoteEntityType;
  onClose: () => void;
  onSubmit: () => Promise<boolean>;
  onValidatingChange?: (isValidating: boolean) => void;
}

// One picked reel against an existing quote gets the Add Line form itself, so it is priced,
// costed and noted exactly as a line added by hand - with the reel's part already chosen.
export const ReelLineDetails = forwardRef(function ReelLineDetails(
  { reel, quoteId, entityType, onClose, onSubmit, onValidatingChange }: IProps,
  ref
) {
  const { record: quote, isLoading } = useGetQuote({
    quoteId: String(quoteId),
    isOrder: QuoteEntityType.ORDER === entityType ? 1 : 0,
  });

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const initialPart = useMemo(
    () => ({
      number: reel?.partNumber,
      manufacturerId: reel?.manufacturerId,
      manufacturerName: reel?.manufacturerName,
    }),
    [reel?.manufacturerId, reel?.manufacturerName, reel?.partNumber]
  );

  const defaultValues = useMemo(
    () =>
      ({
        part: initialPart,
        manufacturer: { id: reel?.manufacturerId, name: reel?.manufacturerName },
        description: reel?.description ?? '',
        quantity: null,
        uom: reel?.uom ?? null,
      }) as unknown as IQuotePartLineForm,
    [initialPart, reel]
  );

  if (isLoading || !quote) {
    return (
      <Stack sx={{ alignItems: 'center', py: 2 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <SelectedQuotePartProvider>
      <LineDetails
        ref={ref}
        quoteId={quoteId}
        quoteType={QuoteType.CUSTOMER}
        quotePart={null}
        isEdit={false}
        isOpen
        onClose={onClose}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        entityType={entityType}
        quote={quote as ICustomerQuote}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        onValidatingChange={onValidatingChange}
        initialPart={initialPart}
      />
    </SelectedQuotePartProvider>
  );
});
